import { lazy } from 'react'
import { Navigate } from 'react-router'

import Cart from '@/pages/Cart'
import CheckoutPage from '@/pages/CheckoutPage'
import ContactFormPage from '@/pages/ContactFormPage/wrapper'
import LandingPage from '@/pages/LandingPage'
import MyMachinesPage from '@/pages/MyMachinesPage'
import MyWorkspacePage from '@/pages/MyWorkspacePage'
import OrderConfirmationPage from '@/pages/OrderConfirmationPage'
import OrderDetailsPage from '@/pages/OrderPage/OrderDetailsPage'
import OrdersHistoryPage from '@/pages/OrderPage/OrdersHistoryPage'
import DataProtection from '@/pages/PolicyPage/DataProtection'
import GeneralTermsAndConditions from '@/pages/PolicyPage/GeneralTerm'
import Imprint from '@/pages/PolicyPage/Imprint'
import ProductComparison from '@/pages/ProductComparison'
import ProductDetailPage from '@/pages/ProductDetailPage'
import ProductMatchingPage from '@/pages/ProductMatchingPage'
import CategoryOverviewPage from '@/pages/ProductNavigationPage/CategoryOverviewPage'
import SubCategoryOverviewPage from '@/pages/ProductNavigationPage/SubCategoryOverviewPage'
import SubCategoryPage from '@/pages/ProductNavigationPage/SubCategoryPage/SubCategoryPage'
import ProfileChangeInvoicePage from '@/pages/ProfileChangeInvoicePage'
import ProfilePage from '@/pages/ProfilePage'
import RequestServiceConfirmationPage from '@/pages/RequestServiceConfirmationPage'
import RequestServicePage from '@/pages/RequestServicePage'
import SearchResultsPage from '@/pages/SearchResultsPage'
import SearchResultsDetailPage from '@/pages/SearchResultsPage/Detail'
import ServicePackagesPage from '@/pages/ServicePackagesPage'
import SetPasswordPage from '@/pages/SetPasswordPage'
import SparePartsFinder from '@/pages/SparePartsFinder'
import TechnicalLoginPage from '@/pages/TechnicalLogin'
import WishlistDetailPage from '@/pages/WishlistDetails'
import WishlistOverview from '@/pages/WishlistOverview'
import {
  MODULE_ADMINISTRATION_RIGHTS,
  MODULE_MACHINE_RIGHTS,
  MODULE_ORDER_RIGHTS,
  MODULE_PROFILE_RIGHTS,
  MODULE_PROMOTION_VOUCHER_ADMINISTRATION_RIGHTS,
  MODULE_SERVICE_RIGHTS,
  MODULE_SHOP_NAVIGATION_RIGHTS,
  MODULE_USER_MANAGEMENT_RIGHTS,
  MODULE_WISHLIST_RIGHTS,
} from '@/libs/constants/modulerights'
import { linkGenerator } from '@/libs/utils/language'

import RequireAuth from './RequireAuth'

const AddEmployeeUserPage = lazy(async () => import('@/pages/AddEmployeeUserPage'))
const AddTechnicalUserPage = lazy(async () => import('@/pages/AddTechnicalUserPage'))
const AdminWorkspacePage = lazy(async () => import('@/pages/AdminWorkspacePage'))
const ConfigurationPage = lazy(async () => import('@/pages/ConfigurationPage'))
const ContactReasonManagementPage = lazy(
  async () => import('@/pages/ContactReasonManagement'),
)
const InviteNewUserPage = lazy(async () => import('@/pages/InviteNewUserPage'))
const EmployeeLoginPage = lazy(async () => import('@/pages/EmployeeLogin'))
const ProductManagementPage = lazy(
  async () => import('@/pages/ProductManagementPage'),
)
const PromotionPage = lazy(async () => import('@/pages/PromotionPage'))
const RentalManagement = lazy(async () => import('@/pages/RentalManagement'))
const RoleManagementPage = lazy(async () => import('@/pages/RoleManagementPage'))
const RoleCreatePage = lazy(
  async () => import('@/pages/RoleManagementPage/RoleCreate'),
)
const RoleDetailPage = lazy(
  async () => import('@/pages/RoleManagementPage/RoleDetailPage'),
)
const RoleEditPage = lazy(async () => import('@/pages/RoleManagementPage/RoleEdit'))
const ServiceCaseManagement = lazy(async () => import('@/pages/ServicesManagement'))
const SupportArea = lazy(async () => import('@/pages/SupportArea'))
const UserManagementPage = lazy(async () => import('@/pages/UserManagementPage'))
const EmployeeDetailsPage = lazy(
  async () => import('@/pages/UserManagementPage/EmployeeDetailsPage'),
)
const UserDetailsPage = lazy(
  async () => import('@/pages/UserManagementPage/UserDetailsPage'),
)
const TechnicalUserDetailsPages = lazy(
  async () =>
    import(
      '@/pages/UserManagementPage/UserDetailsPage/TechnicalUserDetailsPage'
    ),
)
const VoucherManagementPage = lazy(async () => import('@/pages/VoucherManagement'))
const VoucherCreate = lazy(
  async () => import('@/pages/VoucherManagement/VoucherCreate'),
)
const VoucherEdit = lazy(async () => import('@/pages/VoucherManagement/VoucherEdit'))
const PunchoutPage = lazy(async () => import('@/pages/Punchout'))
const RegistrationPage = lazy(async () => import('@/pages/RegistrationPage'))
const SelfRegistrationConfirmationPage = lazy(
  async () => import('@/pages/RegistrationPage/SelfRegistrationConfirmationPage'),
)
const SelfRegistrationPage = lazy(
  async () => import('@/pages/RegistrationPage/SelfRegistrationPage'),
)
const UnAuthorizedPage = lazy(async () => import('@/pages/UnAuthorizedPage'))
const RentalPage = lazy(async () => import('@/pages/RentalPage'))

export function generateRoutes() {
  const publicRoutes = [
    {
      path: 'callback',
      element: <Navigate to={linkGenerator('/my-workspace')} replace />,
    },
    {
      path: 'logout',
      element: <Navigate to={linkGenerator('')} replace />,
    },
    {
      path: '',
      element: <LandingPage />,
    },
    {
      path: 'admin-login',
      element: <EmployeeLoginPage />,
    },
    {
      path: 'technical-login/:punchout_type/*',
      element: <TechnicalLoginPage />,
    },
    {
      path: 'data-protection-policy',
      element: <DataProtection />,
    },
    {
      path: 'imprint',
      element: <Imprint />,
    },
    {
      path: 'general-terms-and-conditions',
      element: <GeneralTermsAndConditions />,
    },
    {
      path: 'self-registration/:userId',
      element: <SelfRegistrationPage />,
    },
    {
      path: 'self-registration-confirmation',
      element: <SelfRegistrationConfirmationPage />,
    },
    {
      path: 'registration/:token/:language/:countryId',
      element: <RegistrationPage />,
    },
    {
      path: 'registration/:token',
      element: <RegistrationPage />,
    },
    {
      path: 'registration',
      element: <RegistrationPage />,
    },
    {
      title: 'welcome welcome-subtext',
      path: 'punchout',
      element: <PunchoutPage />,
    },
    {
      path: 'unauthorized',
      element: <UnAuthorizedPage />,
    },
  ]

  const privateRoutes = [
    {
      path: 'my-workspace',
      element: (
        <RequireAuth>
          <MyWorkspacePage />
        </RequireAuth>
      ),
    },
    {
      path: 'cart',
      element: (
        <RequireAuth
          requiredPermission={MODULE_SHOP_NAVIGATION_RIGHTS.VIEW_CART}
        >
          <Cart
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.shoppingCart',
                path: '/cart',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'checkout',
      element: (
        <RequireAuth>
          <CheckoutPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.shoppingCart',
                path: '/cart',
              },
              {
                title: 'breadcrumbTitle.checkoutCart',
                path: '/checkout',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'confirm-order',
      element: (
        <RequireAuth>
          <OrderConfirmationPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.orderConfirmation',
                path: '/confirm-order',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'orders',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ORDER_RIGHTS.VIEW_ORDER_HISTORY}
        >
          <OrdersHistoryPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.orderHistory',
                path: '/orders',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'orders/:orderNumber',
      element: (
        <RequireAuth requiredPermission={MODULE_ORDER_RIGHTS.VIEW_ORDER_DETAIL}>
          <OrderDetailsPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.orderHistory',
                path: '/orders',
              },
              {
                title: 'breadcrumbTitle.order $1',
                path: '/orders/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'contact-form',
      element: (
        <RequireAuth
          requiredPermission={MODULE_SERVICE_RIGHTS.VIEW_CONTACT_FORM}
        >
          <ContactFormPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.contactForm',
                path: '/contact-form',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'my-machines',
      element: (
        <RequireAuth
          requiredPermission={MODULE_MACHINE_RIGHTS.VIEW_MACHINE_LIST}
        >
          <MyMachinesPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.myMachines',
                path: '/my-machines',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'product/:materialNumber',
      element: (
        <RequireAuth
          requiredPermission={MODULE_SHOP_NAVIGATION_RIGHTS.VIEW_PRODUCT_INFORMATION}
        >
          <ProductDetailPage
            pageId="9"
            title="$1"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productCategoryOverview',
                path: '/categories',
              },
              {
                title: '$1',
                path: '/category/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/category/$1/subcategory/$2',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/category/$1/subcategory/$2/class/$3',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/product/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'accessories/product/:materialNumber',
      element: (
        <RequireAuth
          requiredPermission={MODULE_SHOP_NAVIGATION_RIGHTS.VIEW_PRODUCT_INFORMATION}
        >
          <ProductDetailPage
            pageId="9.1"
            title="$1"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productCategoryOverview',
                path: '/categories',
              },
              {
                title: '$1',
                path: '/categories/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/category/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/category/$1/subcategory/$2',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/category/$1/subcategory/$2/class/$3',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/$1/product/$2',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'detergents/product/:materialNumber',
      element: (
        <RequireAuth
          requiredPermission={MODULE_SHOP_NAVIGATION_RIGHTS.VIEW_PRODUCT_INFORMATION}
        >
          <ProductDetailPage
            pageId="9.1"
            title="$1"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productCategoryOverview',
                path: '/categories',
              },
              {
                title: '$1',
                path: '/categories/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/category/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/category/$1/subcategory/$2',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/category/$1/subcategory/$2/class/$3',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/$1/product/$2',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'product/:materialNumber/:type',
      element: (
        <RequireAuth>
          <ProductMatchingPage
            pageId="10"
            title="pageTitle.productMatching"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.searchResults',
                path: '/product/$1/$2',
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'categories',
      element: (
        <RequireAuth>
          <CategoryOverviewPage
            pageId="11"
            title="pageTitle.productCategoryOverview"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productCategoryOverview',
                path: '/categories',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'categories/:type',
      element: (
        <RequireAuth>
          <CategoryOverviewPage
            pageId="11.1"
            title="$1"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productCategoryOverview',
                path: '/categories',
              },
              {
                title: '$1',
                path: '/categories/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'category/:categoryId',
      element: (
        <RequireAuth>
          <SubCategoryOverviewPage
            pageId="12"
            title="$1"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productCategoryOverview',
                path: '/categories',
              },
              {
                title: '$1',
                path: '/category/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'accessories/category/:categoryId',
      element: (
        <RequireAuth>
          <SubCategoryOverviewPage
            pageId="12"
            title="$1"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productCategoryOverview',
                path: '/categories',
              },
              {
                title: '$1',
                path: '/categories/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/$1/category/$2',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'detergents/category/:categoryId',
      element: (
        <RequireAuth>
          <SubCategoryOverviewPage
            pageId="12"
            title="$1"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productCategoryOverview',
                path: '/categories',
              },
              {
                title: '$1',
                path: '/categories/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/$1/category/$2',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'category/:categoryId/subcategory/:subCategoryId/class/:classTypeId',
      element: (
        <RequireAuth>
          <SubCategoryPage
            pageId="13"
            title="$1"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productCategoryOverview',
                path: '/categories',
              },
              {
                title: '$1',
                path: '/category/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/category/$1/subcategory/$2',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'category/:categoryId/subcategory/:subCategoryId',
      element: (
        <RequireAuth>
          <SubCategoryPage
            pageId="13"
            title="$1"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productCategoryOverview',
                path: '/categories',
              },
              {
                title: '$1',
                path: '/category/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/category/$1/subcategory/$2',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'accessories/category/:categoryId/subcategory/:subCategoryId/class/:classTypeId',
      element: (
        <RequireAuth>
          <SubCategoryPage
            pageId="13"
            title="$1"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productCategoryOverview',
                path: '/categories',
              },
              {
                title: '$1',
                path: '/categories/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/$1/category/$2',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/$1/category/$2/subcategory/$3',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'accessories/category/:categoryId/subcategory/:subCategoryId',
      element: (
        <RequireAuth>
          <SubCategoryPage
            pageId="13"
            title="$1"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productCategoryOverview',
                path: '/categories',
              },
              {
                title: '$1',
                path: '/categories/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/$1/category/$2',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/$1/category/$2/subcategory/$3',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'detergents/category/:categoryId/subcategory/:subCategoryId/class/:classTypeId',
      element: (
        <RequireAuth>
          <SubCategoryPage
            pageId="13"
            title="$1"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productCategoryOverview',
                path: '/categories',
              },
              {
                title: '$1',
                path: '/categories/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/$1/category/$2',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/$1/category/$2/subcategory/$3',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'detergents/category/:categoryId/subcategory/:subCategoryId',
      element: (
        <RequireAuth>
          <SubCategoryPage
            pageId="13"
            title="$1"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productCategoryOverview',
                path: '/categories',
              },
              {
                title: '$1',
                path: '/categories/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/$1/category/$2',
                dynamicTitle: true,
                dynamicPath: true,
              },
              {
                title: '$1',
                path: '/$1/category/$2/subcategory/$3',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'search-results/:searchParam',
      element: (
        <RequireAuth>
          <SearchResultsPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.searchResults',
                path: '/search-results/$1',
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'search-detail/:type/:searchParam',
      element: (
        <RequireAuth>
          <SearchResultsDetailPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.searchResults',
                path: '/search-detail/$1/$2',
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'profile',
      element: (
        <RequireAuth requiredPermission={MODULE_PROFILE_RIGHTS.VIEW_PROFILE}>
          <ProfilePage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.myProfile',
                path: '/profile',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'profile/change-password',
      element: (
        <RequireAuth>
          <SetPasswordPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.myProfile',
                path: '/profile',
              },
              {
                title: 'breadcrumbTitle.changePassword',
                path: '/profile/change-password',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'profile/change-invoice-address',
      element: (
        <RequireAuth>
          <ProfileChangeInvoicePage />
        </RequireAuth>
      ),
    },
    {
      path: 'product-comparison',
      element: (
        <RequireAuth>
          <ProductComparison
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productComparison',
                path: '/product-comparison',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'request-services',
      element: (
        <RequireAuth>
          <RequestServicePage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.requestServices',
                path: '/request-services',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'request-confirmation',
      element: (
        <RequireAuth>
          <RequestServiceConfirmationPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.services',
                path: '/request-services',
              },
              {
                title: 'breadcrumbTitle.createService',
                path: '/request-services',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'service/service-packages',
      element: (
        <RequireAuth
          requiredPermission={MODULE_SHOP_NAVIGATION_RIGHTS.VIEW_SERVICE_PACKAGE_INFORMATION}
        >
          <ServicePackagesPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.services',
                path: undefined,
              },
              {
                title: 'breadcrumbTitle.servicePackages',
                path: '/service/service-packages',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'product-management',
      element: (
        <RequireAuth
          requiredPermission={[
            MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA,
            MODULE_ADMINISTRATION_RIGHTS.VIEW_PRODUCT_PORTFOLIO_MANAGEMENT,
          ]}
        >
          <ProductManagementPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.productManagement',
                path: '/product-management',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'spare-parts',
      element: (
        <RequireAuth
          requiredPermission={MODULE_SHOP_NAVIGATION_RIGHTS.OPEN_SPARE_PART_SHOP}
        >
          <SparePartsFinder
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'pageTitle.sparePartsFinder',
                path: '/spare-parts',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'wishlists',
      element: (
        <RequireAuth requiredPermission={MODULE_WISHLIST_RIGHTS.VIEW_WISHLIST}>
          <WishlistOverview
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'pageTitle.wishlistOverview',
                path: '/wishlists',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'wishlist/:wishlistId/owner/:wishlistOwnerId',
      element: (
        <RequireAuth requiredPermission={MODULE_WISHLIST_RIGHTS.VIEW_WISHLIST}>
          <WishlistDetailPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'pageTitle.wishlistOverview',
                path: '/wishlists',
              },
              {
                title: 'pageTitle.wishlistDetail',
                path: '/wishlist/:wishlistId/owner/:wishlistOwnerId',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'support-area',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ADMINISTRATION_RIGHTS.SUPPORT_AREA}
        >
          <SupportArea
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'pageTitle.supportArea',
                path: '/support-area',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'admin-workspace',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA}
        >
          <AdminWorkspacePage />
        </RequireAuth>
      ),
    },
    {
      path: 'voucher-management',
      element: (
        <RequireAuth
          requiredPermission={[
            MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA,
            MODULE_PROMOTION_VOUCHER_ADMINISTRATION_RIGHTS.VIEW_VOUCHER,
          ]}
        >
          <VoucherManagementPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.voucherManagement',
                path: '/voucher-management',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'promotion-management',
      element: (
        <RequireAuth
          requiredPermission={[
            MODULE_PROMOTION_VOUCHER_ADMINISTRATION_RIGHTS.UPDATE_PROMOTION,
            MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA,
          ]}
        >
          <PromotionPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.promotionManagement',
                path: '/promotion-management',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'user-management',
      element: (
        <RequireAuth
          requiredPermission={[
            MODULE_USER_MANAGEMENT_RIGHTS.VIEW_USER_DETAIL,
            MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA,
          ]}
        >
          <UserManagementPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.userManagement',
                path: '/user-management',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'user-management/:userType',
      element: (
        <RequireAuth
          requiredPermission={[
            MODULE_USER_MANAGEMENT_RIGHTS.VIEW_USER_DETAIL,
            MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA,
          ]}
        >
          <UserManagementPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.userManagement',
                path: '/user-management',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'user-management/technical/:userId',
      element: (
        <RequireAuth
          requiredPermission={[
            MODULE_USER_MANAGEMENT_RIGHTS.VIEW_USER_DETAIL,
            MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA,
          ]}
        >
          <TechnicalUserDetailsPages
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.userManagement',
                path: '/user-management',
              },
              {
                title: '$1',
                path: '/user-management/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'user-management/invitation/:userId',
      element: (
        <RequireAuth
          requiredPermission={[
            MODULE_USER_MANAGEMENT_RIGHTS.VIEW_USER_DETAIL,
            MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA,
          ]}
        >
          <UserDetailsPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.userManagement',
                path: '/user-management',
              },
              {
                title: '$1',
                path: '/user-management/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'user-management/customer/:userId',
      element: (
        <RequireAuth
          requiredPermission={[
            MODULE_USER_MANAGEMENT_RIGHTS.VIEW_USER_DETAIL,
            MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA,
          ]}
        >
          <UserDetailsPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.userManagement',
                path: '/user-management',
              },
              {
                title: '$1',
                path: '/user-management/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'user-management/employee/:userId',
      element: (
        <RequireAuth
          requiredPermission={[
            MODULE_USER_MANAGEMENT_RIGHTS.VIEW_USER_DETAIL,
            MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA,
          ]}
        >
          <EmployeeDetailsPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.userManagement',
                path: '/user-management',
              },
              {
                title: '$1',
                path: '/user-management/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'user-invitation',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA}
        >
          <InviteNewUserPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'pageTitle.userManagement',
                path: '/user-management',
              },
              {
                title: 'pageTitle.inviteNewUser',
                path: '/user-invitation',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'contact-reasons',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ADMINISTRATION_RIGHTS.CRUD_CONTACT_REASON}
        >
          <ContactReasonManagementPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.contactReasons',
                path: '/contact-reasons',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'configuration-management',
      element: (
        <RequireAuth
          requiredPermission={[
            MODULE_ADMINISTRATION_RIGHTS.VIEW_CONFIGURATION,
            MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA,
          ]}
        >
          <ConfigurationPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.configurations',
                path: '/configuration-management',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'service-management',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA}
        >
          <ServiceCaseManagement
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.serviceManagement',
                path: '/service-management',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'add-employee-user',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA}
        >
          <AddEmployeeUserPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'pageTitle.userManagement',
                path: '/user-management',
              },
              {
                title: 'pageTitle.addUser',
                path: '/add-employee-user',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'add-technical-user',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA}
        >
          <AddTechnicalUserPage
            pageId="39"
            title="pageTitle.addUser"
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'pageTitle.userManagement',
                path: '/user-management',
              },
              {
                title: 'pageTitle.addUser',
                path: '/add-technical-user',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'roles-management',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA}
        >
          <RoleManagementPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'pageTitle.roleManagement',
                path: '/roles-management',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'roles-management/create-role',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA}
        >
          <RoleCreatePage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'pageTitle.createRole',
                path: '/roles-management/create-role',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'roles-management/:roleId',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA}
        >
          <RoleDetailPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'pageTitle.roleManagement',
                path: '/roles-management',
              },
              {
                title: '$1',
                path: '/roles-management/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'roles-management/edit/:roleId',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA}
        >
          <RoleEditPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'pageTitle.roleManagement',
                path: '/roles-management',
              },
              {
                title: '$1',
                path: '/roles-management/$1',
                dynamicTitle: true,
                dynamicPath: true,
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'vouchers/create',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA}
        >
          <VoucherCreate
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.voucherManagement',
                path: '/voucher-management',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'voucher/:voucherId',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA}
        >
          <VoucherEdit
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.voucherManagement',
                path: '/voucher-management',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'rental',
      element: (
        <RequireAuth>
          <RentalPage
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.rental',
                path: '/rental',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
    {
      path: 'rental-management',
      element: (
        <RequireAuth
          requiredPermission={MODULE_ADMINISTRATION_RIGHTS.VIEW_ADMIN_AREA}
        >
          <RentalManagement
            breadcrumbs={[
              {
                title: 'breadcrumbTitle.home',
                path: '',
              },
              {
                title: 'breadcrumbTitle.rentalManagement',
                path: '/rental-management',
              },
            ]}
          />
        </RequireAuth>
      ),
    },
  ]

  return { publicRoutes, privateRoutes }
}
